import {
  ReactNode,
  useEffect,
  useState,
  createContext,
  useContext,
  useCallback,
  useRef,
} from "react";

type RampStatus = "idle" | "loading" | "ready" | "error";

type RampPath =
  | "player_w1000plus"
  | "player_w500_999"
  | "player_w320_499"
  | "player_wUnder320";

interface RampContextValue {
  status: RampStatus;
  showPlayingAds: (playerWidth: number) => void;
  showPauseAd: (playerWidth: number) => void;
}

const RampContext = createContext<RampContextValue>({
  status: "idle",
  showPlayingAds: () => {},
  showPauseAd: () => {},
});

const PUB_ID = "1025616";
const WEBSITE_ID = "76367";

const getPathForWidth = (width: number): RampPath => {
  if (width >= 1000) return "player_w1000plus";
  if (width >= 500) return "player_w500_999";
  if (width >= 320) return "player_w320_499";
  return "player_wUnder320";
};

const RampProvider = ({ children }: { children: ReactNode }) => {
  const [status, setStatus] = useState<RampStatus>("idle");
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    if (scriptLoadedRef.current) return;
    scriptLoadedRef.current = true;
    setStatus("loading");

    console.log(
      "[Ramp] Initializing — PUB_ID:",
      PUB_ID,
      "WEBSITE_ID:",
      WEBSITE_ID,
    );

    window.ramp = window.ramp || {};
    window.ramp.que = window.ramp.que || [];
    window.ramp.passiveMode = true;

    console.log("[Ramp] passiveMode set, loading script...");

    const script = document.createElement("script");
    script.src = `https://cdn.intergient.com/${PUB_ID}/${WEBSITE_ID}/ramp.js`;
    script.async = true;

    script.onload = () => {
      console.log("[Ramp] Script loaded successfully");
      window.ramp.que.push(() => {
        console.log("[Ramp] Calling spaNewPage:", window.location.pathname);
        window.ramp.spaNewPage(window.location.pathname);
      });
      setStatus("ready");
      console.log("[Ramp] Status set to ready");
    };

    script.onerror = (err) => {
      console.error("[Ramp] Script failed to load:", err);
      setStatus("error");
    };

    document.body.appendChild(script);
  }, []);

  const showPlayingAds = useCallback(
    (playerWidth: number) => {
      console.log(
        "[Ramp] showPlayingAds called — status:",
        status,
        "playerWidth:",
        playerWidth,
      );
      if (status !== "ready") {
        console.log("[Ramp] showPlayingAds skipped — not ready");
        return;
      }

      const path = getPathForWidth(playerWidth);
      console.log("[Ramp] showPlayingAds — path:", path);
      console.log(
        "[Ramp] showPlayingAds — requesting: standard_iab_foot1, standard_iab_head1",
      );

      window.ramp.que.push(() => {
        console.log("[Ramp] spaAds executing — playing ads, path:", path);
        window.ramp.spaAds({
          ads: [{ type: "standard_iab_foot1" }, { type: "standard_iab_head1" }],
          countPageview: false,
          path,
        });
      });
    },
    [status],
  );

  const showPauseAd = useCallback(
    (playerWidth: number) => {
      console.log(
        "[Ramp] showPauseAd called — status:",
        status,
        "playerWidth:",
        playerWidth,
      );
      if (status !== "ready") {
        console.log("[Ramp] showPauseAd skipped — not ready");
        return;
      }

      const path = getPathForWidth(playerWidth);
      console.log(
        "[Ramp] showPauseAd — path:",
        path,
        "requesting: standard_iab_modl1",
      );

      window.ramp.que.push(() => {
        console.log("[Ramp] spaAds executing — pause ad, path:", path);
        window.ramp.spaAds({
          ads: [{ type: "standard_iab_modl1" }],
          countPageview: false,
          path,
        });
      });
    },
    [status],
  );

  return (
    <RampContext.Provider value={{ status, showPlayingAds, showPauseAd }}>
      {children}
    </RampContext.Provider>
  );
};

function useRamp(): RampContextValue {
  return useContext(RampContext);
}

export { RampProvider, useRamp };
